import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart3,
  Bell,
  BookOpenCheck,
  Building2,
  ClipboardCheck,
  CreditCard,
  Flag,
  GraduationCap,
  LifeBuoy,
  ShieldCheck,
  Star,
  UserCog,
  UsersRound,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import DemoClassPanel from '../../components/education/DemoClassPanel';
import educationOpsService, {
  EducationRecordPayload,
  EducationWorkflowKind,
  EducationWorkflowRecord,
  getEducationRecordId,
} from '../../services/educationOps.service';
import {
  dashboardDataService,
  normalizeEntityId,
  TeacherSelectionApi,
} from '../../services/dashboard-data.service';

type FieldType = 'text' | 'textarea' | 'select' | 'number' | 'datetime-local';

interface WorkflowField {
  name: string;
  label: string;
  type?: FieldType;
  placeholder?: string;
  options?: string[];
  topLevel?: boolean;
}

interface WorkflowConfig {
  kind: EducationWorkflowKind;
  title: string;
  eyebrow: string;
  description: string;
  createLabel: string;
  initialStatus: string;
  statusOptions: string[];
  fields: WorkflowField[];
  list?: () => Promise<EducationWorkflowRecord[]>;
  create?: (payload: EducationRecordPayload) => Promise<EducationWorkflowRecord>;
}

const moduleLinks = [
  { to: '/parent/dashboard', label: 'Parent Dashboard', icon: UsersRound },
  { to: '/education/institute', label: 'Coaching Institute', icon: Building2 },
  { to: '/student/requirements', label: 'Student Requirements', icon: ClipboardCheck },
  { to: '/education/demos', label: 'Demo Classes', icon: GraduationCap },
  { to: '/education/homework', label: 'Homework', icon: BookOpenCheck },
  { to: '/education/complaints', label: 'Complaints and Reviews', icon: Flag },
  { to: '/admin/education/reviews', label: 'Review Moderation', icon: Star },
  { to: '/education/subscription-usage', label: 'Subscription Usage', icon: Star },
  { to: '/admin/education/verification', label: 'Verification', icon: ShieldCheck },
  { to: '/admin/education/refunds', label: 'Refunds', icon: LifeBuoy },
  { to: '/admin/education/staff', label: 'Staff RBAC', icon: UserCog },
  { to: '/admin/education/analytics', label: 'Analytics', icon: BarChart3 },
  { to: '/admin/education/reminders', label: 'Reminders', icon: Bell },
  { to: '/admin/education/payments', label: 'Payment Checks', icon: CreditCard },
];

const statusClass = (status: string) => {
  const value = status.toUpperCase();
  if (['APPROVED', 'ACTIVE', 'VERIFIED', 'ATTENDANCE_MARKED', 'COMPLETED', 'RESOLVED', 'RECONCILED', 'REVIEWED'].includes(value)) {
    return 'border-emerald-200 bg-emerald-50 text-emerald-800';
  }
  if (['REJECTED', 'FAILED', 'CANCELLED', 'HIDDEN', 'LIMIT_EXCEEDED'].includes(value)) {
    return 'border-rose-200 bg-rose-50 text-rose-800';
  }
  if (['PENDING', 'FLAGGED', 'REPORTED', 'REQUESTED', 'DRAFT', 'OPEN', 'SCHEDULED', 'UNDER_REVIEW', 'PENDING_REVIEW'].includes(value)) {
    return 'border-amber-200 bg-amber-50 text-amber-800';
  }
  return 'border-slate-200 bg-slate-50 text-slate-700';
};

const toDisplayDate = (value?: string) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString();
};

const currentUserId = (user: any) => String((user?.user ? user.user : user)?.id || '').trim();
const currentUserRole = (user: any) => String((user?.user ? user.user : user)?.role || '').trim();

const buildPayload = (
  form: Record<string, string>,
  config: WorkflowConfig,
  user: any,
): EducationRecordPayload => {
  const nestedPayload = config.fields.reduce<Record<string, any>>((acc, field) => {
    if (field.topLevel) return acc;
    const value = form[field.name];
    if (value !== undefined && value !== '') acc[field.name] = value;
    return acc;
  }, {});

  return {
    status: form.status || config.initialStatus,
    title: form.title || config.title,
    description: form.description || form.notes || config.description,
    studentUserId: form.studentUserId || (config.kind === 'STUDENT_REQUIREMENT' ? currentUserId(user) : undefined),
    parentUserId: form.parentUserId || (config.kind === 'PARENT_PROFILE' ? currentUserId(user) : undefined),
    teacherUserId:
      form.teacherUserId ||
      (['INSTITUTE_PROFILE', 'TUTOR_VERIFICATION', 'HOMEWORK_TASK'].includes(config.kind)
        ? currentUserId(user)
        : undefined),
    instituteUserId: form.instituteUserId || (config.kind === 'INSTITUTE_PROFILE' ? currentUserId(user) : undefined),
    assignedToUserId: form.assignedToUserId || form.studentUserId || undefined,
    relatedRecordId: form.relatedRecordId || undefined,
    idempotencyKey: form.idempotencyKey || undefined,
    scheduledAt: form.scheduledAt || undefined,
    dueAt: form.dueAt || undefined,
    payload: {
      ...nestedPayload,
      limits: config.kind === 'SUBSCRIPTION_USAGE'
        ? {
            CONTACT: Number(form.contactLimit || 0),
            DEMO: Number(form.demoLimit || 0),
            REPORT: Number(form.reportLimit || 0),
          }
        : undefined,
      usage: config.kind === 'SUBSCRIPTION_USAGE' ? { CONTACT: 0, DEMO: 0, REPORT: 0 } : undefined,
    },
    note: form.note || undefined,
  };
};

const WorkflowConsole: React.FC<{ config: WorkflowConfig }> = ({ config }) => {
  const { user } = useAuth();
  const [records, setRecords] = useState<EducationWorkflowRecord[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [form, setForm] = useState<Record<string, string>>({ status: config.initialStatus });
  const [statusById, setStatusById] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [nextRecords, nextSummary] = await Promise.all([
        config.list ? config.list() : educationOpsService.listRecords({ kind: config.kind }),
        educationOpsService.summary(),
      ]);
      setRecords(nextRecords);
      setSummary(nextSummary);
      setStatusById(
        nextRecords.reduce<Record<string, string>>((acc, record) => {
          acc[getEducationRecordId(record)] = record.status;
          return acc;
        }, {}),
      );
    } finally {
      setLoading(false);
    }
  }, [config]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleChange = (name: string, value: string) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setMessage('');
    const payload = buildPayload(form, config, user);
    try {
      if (config.create) {
        await config.create(payload);
      } else if (config.kind === 'PAYMENT_RECONCILIATION' && form.webhookMode === 'WEBHOOK') {
        await educationOpsService.createPaymentWebhookCheck(payload);
      } else {
        await educationOpsService.createRecord(config.kind, payload);
      }
      setForm({ status: config.initialStatus });
      setMessage('Saved successfully.');
      await load();
    } catch (error: any) {
      setMessage(error?.response?.data?.message || 'Could not save this item.');
    }
  };

  const updateStatus = async (record: EducationWorkflowRecord) => {
    const id = getEducationRecordId(record);
    if (!id) return;
    setMessage('');
    try {
      await educationOpsService.updateRecord(id, {
        status: statusById[id] || record.status,
        action: `${config.kind}_STATUS_UPDATED`,
        payload: { changedFromUi: true },
      });
      setMessage('Status updated.');
      await load();
    } catch (error: any) {
      setMessage(error?.response?.data?.message || 'Could not update status.');
    }
  };

  const consumeUsage = async (record: EducationWorkflowRecord, metric: string) => {
    const id = getEducationRecordId(record);
    if (!id) return;
    await educationOpsService.consumeSubscriptionUsage(id, metric, 1);
    await load();
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-indigo-700">{config.eyebrow}</p>
            <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-950">{config.title}</h1>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">{config.description}</p>
          </div>
          <Link
            to="/education/operations"
            className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-100"
          >
            Operations Hub
          </Link>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Total Records</p>
            <p className="mt-2 text-2xl font-semibold text-slate-950">{summary?.total ?? records.length}</p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">This Area</p>
            <p className="mt-2 text-2xl font-semibold text-slate-950">{records.length}</p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Open Complaints</p>
            <p className="mt-2 text-2xl font-semibold text-slate-950">{summary?.openComplaints ?? 0}</p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Pending Demos</p>
            <p className="mt-2 text-2xl font-semibold text-slate-950">{summary?.pendingDemos ?? 0}</p>
          </div>
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-[420px_minmax(0,1fr)]">
          <form onSubmit={handleSubmit} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-5">
              <h2 className="text-lg font-semibold text-slate-950">{config.createLabel}</h2>
              <p className="mt-1 text-sm text-slate-500">Data is saved with status history and audit context.</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Title</label>
                <input
                  value={form.title || ''}
                  onChange={(event) => handleChange('title', event.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                  placeholder={config.title}
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Status</label>
                <select
                  value={form.status || config.initialStatus}
                  onChange={(event) => handleChange('status', event.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                >
                  {config.statusOptions.map((status) => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>

              {config.fields.map((field) => (
                <div key={field.name}>
                  <label className="mb-1 block text-sm font-medium text-slate-700">{field.label}</label>
                  {field.type === 'textarea' ? (
                    <textarea
                      value={form[field.name] || ''}
                      onChange={(event) => handleChange(field.name, event.target.value)}
                      rows={4}
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                      placeholder={field.placeholder}
                    />
                  ) : field.type === 'select' ? (
                    <select
                      value={form[field.name] || ''}
                      onChange={(event) => handleChange(field.name, event.target.value)}
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                    >
                      <option value="">Select</option>
                      {(field.options || []).map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={field.type || 'text'}
                      value={form[field.name] || ''}
                      onChange={(event) => handleChange(field.name, event.target.value)}
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                      placeholder={field.placeholder}
                    />
                  )}
                </div>
              ))}
            </div>

            <button
              type="submit"
              className="mt-5 inline-flex w-full items-center justify-center rounded-lg bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-indigo-700"
            >
              Save
            </button>
            {message && <p className="mt-3 rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-700">{message}</p>}
          </form>

          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-950">Records</h2>
                <p className="mt-1 text-sm text-slate-500">Review lifecycle status, audit history, and important fields.</p>
              </div>
              <button
                type="button"
                onClick={() => void load()}
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Refresh
              </button>
            </div>

            {loading ? (
              <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">Loading...</div>
            ) : records.length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">No records yet.</div>
            ) : (
              <div className="space-y-3">
                {records.map((record) => {
                  const id = getEducationRecordId(record);
                  const history = record.history || [];
                  return (
                    <article key={id || record.title} className="rounded-lg border border-slate-200 p-4">
                      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="font-semibold text-slate-950">{record.title || record.kind}</h3>
                            <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${statusClass(record.status)}`}>
                              {record.status}
                            </span>
                            {record.receiptNumber && (
                              <span className="rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-xs font-semibold text-sky-800">
                                {record.receiptNumber}
                              </span>
                            )}
                          </div>
                          <p className="mt-1 text-sm text-slate-600">{record.description || record.payload?.notes || 'No description added.'}</p>
                          <div className="mt-3 grid gap-2 text-xs text-slate-500 sm:grid-cols-2">
                            {record.studentUserId && <span>Student: {record.studentUserId}</span>}
                            {record.teacherUserId && <span>Teacher: {record.teacherUserId}</span>}
                            {record.assignedToUserId && <span>Assigned: {record.assignedToUserId}</span>}
                            {record.dueAt && <span>Due: {toDisplayDate(record.dueAt)}</span>}
                            {record.scheduledAt && <span>Scheduled: {toDisplayDate(record.scheduledAt)}</span>}
                            {record.idempotencyKey && <span>Idempotency: {record.idempotencyKey}</span>}
                          </div>
                        </div>
                        <div className="flex min-w-[220px] flex-col gap-2">
                          <select
                            value={statusById[id] || record.status}
                            onChange={(event) => setStatusById((prev) => ({ ...prev, [id]: event.target.value }))}
                            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
                          >
                            {config.statusOptions.map((status) => (
                              <option key={status} value={status}>{status}</option>
                            ))}
                          </select>
                          <button
                            type="button"
                            onClick={() => void updateStatus(record)}
                            className="rounded-lg bg-slate-950 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
                          >
                            Update Status
                          </button>
                          {config.kind === 'SUBSCRIPTION_USAGE' && (
                            <div className="grid grid-cols-3 gap-1">
                              {['CONTACT', 'DEMO', 'REPORT'].map((metric) => (
                                <button
                                  key={metric}
                                  type="button"
                                  onClick={() => void consumeUsage(record, metric)}
                                  className="rounded-md border border-slate-200 px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50"
                                >
                                  +{metric}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      {history.length > 0 && (
                        <details className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
                          <summary className="cursor-pointer font-semibold text-slate-700">History</summary>
                          <div className="mt-2 space-y-1">
                            {history.slice(-5).map((entry, index) => (
                              <p key={`${entry.createdAt}-${index}`}>
                                {entry.action} {entry.status ? `-> ${entry.status}` : ''} by {entry.actorUserId} on {toDisplayDate(entry.createdAt)}
                              </p>
                            ))}
                          </div>
                        </details>
                      )}
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const EducationOperationsHubPage: React.FC = () => {
  const [summary, setSummary] = useState<any>(null);

  useEffect(() => {
    educationOpsService.summary().then(setSummary).catch(() => setSummary(null));
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-indigo-700">Education Operations</p>
          <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-950">Education module control center</h1>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
            Manage parent access, institute profiles, requirements, demos, verification, refunds, homework, complaints, staff permissions, analytics, reminders, payment checks, and subscription usage.
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {moduleLinks.map((item) => {
            const Icon = item.icon;
            return (
              <Link key={item.to} to={item.to} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
                <Icon className="h-5 w-5 text-indigo-700" />
                <p className="mt-4 font-semibold text-slate-950">{item.label}</p>
              </Link>
            );
          })}
        </div>
        {summary?.recommendations?.length > 0 && (
          <div className="mt-6 rounded-lg border border-indigo-200 bg-indigo-50 p-4">
            <p className="font-semibold text-indigo-950">Smart recommendations</p>
            <div className="mt-2 space-y-1 text-sm text-indigo-900">
              {summary.recommendations.map((item: string) => <p key={item}>{item}</p>)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const commonRequirementFields: WorkflowField[] = [
  { name: 'classLevel', label: 'Class or Level', placeholder: 'Class 8, Intermediate, B.Tech, Skill course' },
  { name: 'subject', label: 'Subject', placeholder: 'Mathematics, Physics, Spoken English' },
  { name: 'board', label: 'Board / Curriculum', placeholder: 'CBSE, SSC, ICSE, State Board' },
  { name: 'teachingMode', label: 'Teaching Mode', type: 'select', options: ['ONLINE', 'OFFLINE', 'HYBRID'] },
  { name: 'location', label: 'Location', placeholder: 'Village, mandal, colony, town, or city' },
  { name: 'radiusKm', label: 'Radius KM', type: 'select', options: ['5', '10', '15', '20'] },
  { name: 'budget', label: 'Budget', placeholder: 'Monthly or hourly budget' },
  { name: 'language', label: 'Preferred Language', placeholder: 'Telugu, Hindi, English' },
  { name: 'availability', label: 'Availability', placeholder: 'Weekdays 6 PM, Sunday morning' },
  { name: 'notes', label: 'Learning Goals / Notes', type: 'textarea' },
];

export const ParentDashboardPage = () => (
  <WorkflowConsole
    config={{
      kind: 'PARENT_PROFILE',
      title: 'Parent and Guardian Dashboard',
      eyebrow: 'Parent Access',
      description: 'Create parent profile records, manage child learning preferences, approvals, communication permissions, safety controls, reports, and payment visibility.',
      createLabel: 'Save Parent Profile',
      initialStatus: 'ACTIVE',
      statusOptions: ['ACTIVE', 'PENDING_CONSENT', 'SUSPENDED', 'ARCHIVED'],
      list: educationOpsService.getParentProfile,
      create: educationOpsService.saveParentProfile,
      fields: [
        { name: 'studentUserId', label: 'Managed Student User ID', topLevel: true },
        { name: 'relation', label: 'Relation to Student', placeholder: 'Father, mother, guardian' },
        { name: 'communicationPermission', label: 'Communication Permission', type: 'select', options: ['APPROVED', 'RESTRICTED', 'PARENT_ONLY'] },
        { name: 'paymentControl', label: 'Payment Control', type: 'select', options: ['PARENT_APPROVAL_REQUIRED', 'STUDENT_ALLOWED', 'DISABLED'] },
        { name: 'notes', label: 'Parent Notes', type: 'textarea' },
      ],
    }}
  />
);

export const CoachingInstitutePage = () => (
  <WorkflowConsole
    config={{
      kind: 'INSTITUTE_PROFILE',
      title: 'Coaching Institute Module',
      eyebrow: 'Institute Operations',
      description: 'Register institute details, branches, courses, batches, tutors, demo sessions, enquiries, attendance, payments, and reports.',
      createLabel: 'Save Institute Profile',
      initialStatus: 'DRAFT',
      statusOptions: ['DRAFT', 'PENDING_VERIFICATION', 'APPROVED', 'REJECTED', 'SUSPENDED'],
      list: educationOpsService.getInstituteProfile,
      create: educationOpsService.saveInstituteProfile,
      fields: [
        { name: 'instituteName', label: 'Institute Name', placeholder: 'Local coaching center name' },
        { name: 'branches', label: 'Branches', type: 'textarea', placeholder: 'Branch names and addresses' },
        { name: 'courses', label: 'Courses and Batches', type: 'textarea', placeholder: 'Course, batch timing, fee' },
        { name: 'tutors', label: 'Tutor Team', type: 'textarea', placeholder: 'Tutor names, subjects, experience' },
        { name: 'contactPolicy', label: 'Contact Policy', type: 'select', options: ['IN_APP_ONLY', 'AFTER_DEMO', 'AFTER_PAYMENT'] },
      ],
    }}
  />
);

export const StudentRequirementsPage = () => (
  <WorkflowConsole
    config={{
      kind: 'STUDENT_REQUIREMENT',
      title: 'Student Requirement Workflow',
      eyebrow: 'Matching Input',
      description: 'Capture tutor requirements as drafts or submitted requests with class, subject, mode, budget, language, timings, and locality.',
      createLabel: 'Create Requirement',
      initialStatus: 'DRAFT',
      statusOptions: ['DRAFT', 'SUBMITTED', 'MATCHING', 'MATCHED', 'DEMO_REQUESTED', 'CONVERTED', 'CLOSED'],
      fields: commonRequirementFields,
    }}
  />
);

export const DemoClassesPage: React.FC = () => {
  const { user } = useAuth();
  const role = currentUserRole(user);

  if (role !== 'ADMIN') {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <DemoClassPanel />
        </div>
      </div>
    );
  }

  return (
    <WorkflowConsole
      config={{
        kind: 'DEMO_CLASS',
        title: 'Demo Class Lifecycle',
        eyebrow: 'Demo Scheduling',
        description: 'Track demo requests through proposed slots, tutor acceptance, rejection, reschedule, completion, feedback, and conversion.',
        createLabel: 'Create Demo Record',
        initialStatus: 'REQUESTED',
        statusOptions: ['REQUESTED', 'ACCEPTED', 'REJECTED', 'RESCHEDULED', 'ATTENDANCE_MARKED', 'COMPLETED', 'FEEDBACK_SUBMITTED', 'CONVERTED', 'CANCELLED'],
        fields: [
          { name: 'studentUserId', label: 'Student User ID', topLevel: true },
          { name: 'teacherUserId', label: 'Tutor User ID', topLevel: true },
          { name: 'subject', label: 'Subject' },
          { name: 'classLevel', label: 'Class / Level' },
          { name: 'mode', label: 'Mode', type: 'select', options: ['ONLINE', 'OFFLINE', 'HYBRID'] },
          { name: 'scheduledAt', label: 'Preferred Slot', type: 'datetime-local', topLevel: true },
          { name: 'proposedSlots', label: 'Additional Proposed Slots', type: 'textarea' },
          { name: 'reminderChannels', label: 'Reminder Channels', placeholder: 'IN_APP, SMS, WHATSAPP' },
          { name: 'studentAttended', label: 'Student Attendance', type: 'select', options: ['YES', 'NO'] },
          { name: 'teacherAttended', label: 'Tutor Attendance', type: 'select', options: ['YES', 'NO'] },
          { name: 'durationMinutes', label: 'Duration Minutes', type: 'number' },
          { name: 'demoFee', label: 'Demo Fee', type: 'number' },
          { name: 'conversionStatus', label: 'Demo-to-Paid Conversion', type: 'select', options: ['NOT_READY', 'FOLLOW_UP', 'CONVERTED', 'DECLINED'] },
          { name: 'notes', label: 'Notes', type: 'textarea' },
        ],
      }}
    />
  );
};

export const TutorVerificationPage = () => (
  <WorkflowConsole
    config={{
      kind: 'TUTOR_VERIFICATION',
      title: 'Tutor Verification Records',
      eyebrow: 'Trust and Safety',
      description: 'Maintain separate verification records, rejection reasons, resubmissions, badge readiness, signed document references, and audit history.',
      createLabel: 'Submit Verification Record',
      initialStatus: 'PENDING',
      statusOptions: ['PENDING', 'UNDER_REVIEW', 'APPROVED', 'REJECTED', 'RESUBMITTED', 'EXPIRED'],
      fields: [
        { name: 'teacherUserId', label: 'Tutor User ID', topLevel: true },
        { name: 'verificationType', label: 'Verification Type', type: 'select', options: ['MOBILE', 'EMAIL', 'IDENTITY', 'QUALIFICATION', 'EXPERIENCE', 'BACKGROUND', 'LOCALALLY_ASSESSED'] },
        { name: 'identityStatus', label: 'Identity Check', type: 'select', options: ['PENDING', 'UNDER_REVIEW', 'APPROVED', 'REJECTED', 'EXPIRED'] },
        { name: 'qualificationStatus', label: 'Qualification Check', type: 'select', options: ['PENDING', 'UNDER_REVIEW', 'APPROVED', 'REJECTED', 'EXPIRED'] },
        { name: 'experienceStatus', label: 'Experience Check', type: 'select', options: ['PENDING', 'UNDER_REVIEW', 'APPROVED', 'REJECTED', 'EXPIRED'] },
        { name: 'backgroundStatus', label: 'Background Review', type: 'select', options: ['PENDING', 'UNDER_REVIEW', 'APPROVED', 'REJECTED', 'EXPIRED'] },
        { name: 'documentName', label: 'Document Name' },
        { name: 'documentStatus', label: 'Document Status', type: 'select', options: ['NOT_SUBMITTED', 'SUBMITTED', 'APPROVED', 'REJECTED', 'EXPIRED'] },
        { name: 'signedDocumentUrl', label: 'Signed Document URL' },
        { name: 'badgeStatus', label: 'Badge Status', type: 'select', options: ['NOT_ELIGIBLE', 'ELIGIBLE', 'ACTIVE', 'EXPIRED', 'REVOKED'] },
        { name: 'rejectionReason', label: 'Rejection Reason', type: 'textarea' },
        { name: 'expiresAt', label: 'Expiry / Reverification Date' },
        { name: 'note', label: 'Review Note', type: 'textarea' },
      ],
    }}
  />
);

export const RefundWorkflowPage = () => (
  <WorkflowConsole
    config={{
      kind: 'REFUND_REQUEST',
      title: 'Refund Workflow',
      eyebrow: 'Payments',
      description: 'Track refund eligibility, required proof, admin review, refund amount, deadline, status, and reconciliation notes.',
      createLabel: 'Create Refund Request',
      initialStatus: 'REQUESTED',
      statusOptions: ['REQUESTED', 'UNDER_REVIEW', 'APPROVED', 'REJECTED', 'PAID', 'CLOSED'],
      fields: [
        { name: 'studentUserId', label: 'Student User ID', topLevel: true },
        { name: 'relatedRecordId', label: 'Payment / Subscription ID', topLevel: true },
        { name: 'refundReason', label: 'Refund Reason', type: 'select', options: ['NO_TUTOR_MATCH', 'DEMO_NOT_COMPLETED', 'PAYMENT_FAILURE', 'DUPLICATE_PAYMENT', 'SERVICE_ISSUE', 'OTHER'] },
        { name: 'amount', label: 'Requested Amount', type: 'number' },
        { name: 'proof', label: 'Proof / Notes', type: 'textarea' },
      ],
    }}
  />
);

export const HomeworkTasksPage: React.FC = () => {
  const { user } = useAuth();
  const role = currentUserRole(user);
  const userId = currentUserId(user);
  const canAssign = role === 'TEACHER' || role === 'ADMIN';
  const canReview = role === 'TEACHER' || role === 'ADMIN';
  const canSubmit = role === 'STUDENT' || role === 'ADMIN';
  const [records, setRecords] = useState<EducationWorkflowRecord[]>([]);
  const [teacherSelections, setTeacherSelections] = useState<TeacherSelectionApi[]>([]);
  const [form, setForm] = useState({
    title: '',
    studentUserId: '',
    studentUserIds: [] as string[],
    teacherUserId: '',
    subject: '',
    instructions: '',
    attachmentUrl: '',
    dueAt: '',
  });
  const [submissionById, setSubmissionById] = useState<Record<string, string>>({});
  const [reviewById, setReviewById] = useState<Record<string, { marks: string; remarks: string }>>({});
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const nextRecords = await educationOpsService.listRecords({ kind: 'HOMEWORK_TASK' });
      setRecords(nextRecords);
      if (role === 'TEACHER') {
        const selections = await dashboardDataService.getTeacherSelections(true);
        setTeacherSelections(Array.isArray(selections) ? selections : []);
      } else {
        setTeacherSelections([]);
      }
    } finally {
      setLoading(false);
    }
  }, [role]);

  useEffect(() => {
    void load();
  }, [load]);

  const studentOptions = useMemo(() => {
    const seen = new Set<string>();
    return teacherSelections
      .map((selection) => {
        const studentId = normalizeEntityId(selection.studentUserId);
        if (!studentId || seen.has(studentId)) return null;
        seen.add(studentId);
        return {
          studentId,
          label: `${selection.studentName || 'Student'} - ${selection.subject}`,
          subject: selection.subject,
          studentName: selection.studentName || 'Student',
          teacherName: selection.teacherName,
        };
      })
      .filter(Boolean) as Array<{
        studentId: string;
        label: string;
        subject: string;
        studentName: string;
        teacherName: string;
      }>;
  }, [teacherSelections]);

  const selectedStudents =
    role === 'TEACHER'
      ? studentOptions.filter((item) => form.studentUserIds.includes(item.studentId))
      : form.studentUserId
        ? [{ studentId: form.studentUserId, label: form.studentUserId, subject: form.subject, studentName: 'Student', teacherName: '' }]
        : [];

  const toggleSelectedStudent = (studentId: string) => {
    setForm((prev) => {
      const exists = prev.studentUserIds.includes(studentId);
      const nextIds = exists
        ? prev.studentUserIds.filter((item) => item !== studentId)
        : [...prev.studentUserIds, studentId];
      const nextSubjects = studentOptions
        .filter((item) => nextIds.includes(item.studentId))
        .map((item) => item.subject)
        .filter(Boolean);
      return {
        ...prev,
        studentUserIds: nextIds,
        subject: prev.subject || Array.from(new Set(nextSubjects)).join(', '),
      };
    });
  };

  const handleCreateHomework = async (event: React.FormEvent) => {
    event.preventDefault();
    setMessage('');
    const resolvedStudentIds =
      role === 'TEACHER'
        ? form.studentUserIds
        : form.studentUserId
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean);
    const resolvedTeacherId = role === 'ADMIN' ? form.teacherUserId.trim() : userId;
    if (resolvedStudentIds.length === 0) {
      setMessage('Please select at least one student before assigning homework.');
      return;
    }
    if (!resolvedTeacherId) {
      setMessage('Teacher context was not found.');
      return;
    }

    try {
      const assignmentBatchId = `hw-${Date.now()}`;
      await Promise.all(
        resolvedStudentIds.map((studentId) => {
          const selectedStudent = studentOptions.find((item) => item.studentId === studentId);
          return educationOpsService.createRecord('HOMEWORK_TASK', {
            status: 'ASSIGNED',
            title: form.title.trim() || `${form.subject || selectedStudent?.subject || 'Homework'} task`,
            description: form.instructions.trim(),
            studentUserId: studentId,
            teacherUserId: resolvedTeacherId,
            assignedToUserId: studentId,
            relatedRecordId: assignmentBatchId,
            dueAt: form.dueAt || undefined,
            payload: {
              subject: form.subject || selectedStudent?.subject || '',
              instructions: form.instructions.trim(),
              attachmentUrl: form.attachmentUrl.trim() || undefined,
              studentName: selectedStudent?.studentName,
              teacherName: selectedStudent?.teacherName,
              assignmentBatchId,
              assignedStudentCount: resolvedStudentIds.length,
            },
          });
        }),
      );
      setForm({
        title: '',
        studentUserId: '',
        studentUserIds: [],
        teacherUserId: '',
        subject: '',
        instructions: '',
        attachmentUrl: '',
        dueAt: '',
      });
      setMessage(`Homework assigned to ${resolvedStudentIds.length} student${resolvedStudentIds.length === 1 ? '' : 's'}.`);
      await load();
    } catch (error: any) {
      setMessage(error?.response?.data?.message || 'Could not assign homework.');
    }
  };

  const handleSubmitHomework = async (record: EducationWorkflowRecord) => {
    const id = getEducationRecordId(record);
    const answer = submissionById[id]?.trim();
    if (!id || !answer) return;
    await educationOpsService.submitHomework(id, { answer });
    setSubmissionById((prev) => ({ ...prev, [id]: '' }));
    setMessage('Homework submitted.');
    await load();
  };

  const handleReviewHomework = async (record: EducationWorkflowRecord) => {
    const id = getEducationRecordId(record);
    const review = reviewById[id] || { marks: '', remarks: '' };
    if (!id) return;
    await educationOpsService.reviewHomework(id, {
      marks: review.marks ? Number(review.marks) : undefined,
      remarks: review.remarks,
    });
    setReviewById((prev) => ({ ...prev, [id]: { marks: '', remarks: '' } }));
    setMessage('Homework reviewed.');
    await load();
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-indigo-700">Learning Management</p>
          <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-950">Homework and learning tasks</h1>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
            Teachers assign work to students from active selections. Students submit answers here, and teachers review marks and remarks.
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-[420px_minmax(0,1fr)]">
          {canAssign && (
            <form onSubmit={handleCreateHomework} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-950">Assign homework</h2>
              <p className="mt-1 text-sm text-slate-500">
                Teacher context is automatic. Pick one or more students from your confirmed selections.
              </p>
              <div className="mt-5 space-y-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Students</label>
                  {role === 'TEACHER' ? (
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                          {form.studentUserIds.length} selected
                        </p>
                        <button
                          type="button"
                          onClick={() =>
                            setForm((prev) => ({
                              ...prev,
                              studentUserIds:
                                prev.studentUserIds.length === studentOptions.length
                                  ? []
                                  : studentOptions.map((item) => item.studentId),
                            }))
                          }
                          className="rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                          disabled={studentOptions.length === 0}
                        >
                          {form.studentUserIds.length === studentOptions.length && studentOptions.length > 0 ? 'Clear All' : 'Select All'}
                        </button>
                      </div>
                      <div className="max-h-56 space-y-2 overflow-y-auto">
                        {studentOptions.map((option) => (
                          <label
                            key={option.studentId}
                            className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 hover:border-indigo-200 hover:bg-indigo-50"
                          >
                            <input
                              type="checkbox"
                              checked={form.studentUserIds.includes(option.studentId)}
                              onChange={() => toggleSelectedStudent(option.studentId)}
                              className="mt-1 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                            />
                            <span>
                              <span className="block font-semibold text-slate-900">{option.studentName}</span>
                              <span className="block text-xs text-slate-500">{option.subject}</span>
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <input
                      value={form.studentUserId}
                      onChange={(event) => setForm((prev) => ({ ...prev, studentUserId: event.target.value }))}
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                      placeholder="Comma-separated student user IDs for admin assignment"
                    />
                  )}
                  {role === 'TEACHER' && studentOptions.length === 0 && (
                    <p className="mt-2 text-xs text-amber-700">Confirmed students will appear here after teacher selection.</p>
                  )}
                  {selectedStudents.length > 1 && (
                    <p className="mt-2 text-xs text-slate-500">
                      This will create separate homework records for each selected student, so submissions and reviews stay individual.
                    </p>
                  )}
                </div>

                {role === 'ADMIN' && (
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">Teacher User ID</label>
                    <input
                      value={form.teacherUserId}
                      onChange={(event) => setForm((prev) => ({ ...prev, teacherUserId: event.target.value }))}
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                      placeholder="Teacher user ID for admin assignment"
                    />
                  </div>
                )}

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Title</label>
                  <input
                    value={form.title}
                    onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                    placeholder="Chapter practice, worksheet, revision task"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Subject</label>
                  <input
                    value={form.subject}
                    onChange={(event) => setForm((prev) => ({ ...prev, subject: event.target.value }))}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                    placeholder="Subject"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Instructions</label>
                  <textarea
                    value={form.instructions}
                    onChange={(event) => setForm((prev) => ({ ...prev, instructions: event.target.value }))}
                    rows={4}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                    placeholder="Problems, chapters, submission notes..."
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Attachment URL</label>
                  <input
                    value={form.attachmentUrl}
                    onChange={(event) => setForm((prev) => ({ ...prev, attachmentUrl: event.target.value }))}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                    placeholder="Worksheet or reference link"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Due Date</label>
                  <input
                    type="datetime-local"
                    value={form.dueAt}
                    onChange={(event) => setForm((prev) => ({ ...prev, dueAt: event.target.value }))}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                  />
                </div>
              </div>
              <button
                type="submit"
                className="mt-5 inline-flex w-full items-center justify-center rounded-lg bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-indigo-700"
              >
                Assign Homework
              </button>
            </form>
          )}

          <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-950">Homework records</h2>
                <p className="mt-1 text-sm text-slate-500">Assigned, submitted, reviewed, and completed tasks.</p>
              </div>
              <button
                type="button"
                onClick={() => void load()}
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Refresh
              </button>
            </div>

            {message && <p className="mb-4 rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-700">{message}</p>}
            {loading ? (
              <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">Loading...</div>
            ) : records.length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">No homework yet.</div>
            ) : (
              <div className="space-y-3">
                {records.map((record) => {
                  const id = getEducationRecordId(record);
                  const review = reviewById[id] || { marks: '', remarks: '' };
                  return (
                    <article key={id} className="rounded-lg border border-slate-200 p-4">
                      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="font-semibold text-slate-950">{record.title || 'Homework task'}</h3>
                            <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${statusClass(record.status)}`}>
                              {record.status}
                            </span>
                          </div>
                          <p className="mt-2 text-sm text-slate-600">{record.payload?.instructions || record.description || 'No instructions added.'}</p>
                          <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-500">
                            <span>Student: {record.payload?.studentName || 'Assigned student'}</span>
                            <span>Subject: {record.payload?.subject || 'General'}</span>
                            {record.dueAt && <span>Due: {toDisplayDate(record.dueAt)}</span>}
                          </div>
                          {record.payload?.attachmentUrl && (
                            <a href={record.payload.attachmentUrl} target="_blank" rel="noreferrer" className="mt-2 inline-flex text-sm font-semibold text-indigo-700 underline">
                              Open attachment
                            </a>
                          )}
                          {record.payload?.submission?.answer && (
                            <div className="mt-3 rounded-lg bg-slate-50 p-3 text-sm text-slate-700">
                              <p className="font-semibold">Submission</p>
                              <p className="mt-1">{record.payload.submission.answer}</p>
                            </div>
                          )}
                          {record.payload?.review && (
                            <div className="mt-3 rounded-lg bg-emerald-50 p-3 text-sm text-emerald-900">
                              <p className="font-semibold">Teacher Review</p>
                              <p className="mt-1">
                                {record.payload.review.marks !== undefined ? `Marks: ${record.payload.review.marks}. ` : ''}
                                {record.payload.review.remarks || ''}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>

                      {canSubmit && ['ASSIGNED', 'LATE'].includes(record.status) && (
                        <div className="mt-4">
                          <label className="mb-1 block text-sm font-medium text-slate-700">Submit answer</label>
                          <textarea
                            value={submissionById[id] || ''}
                            onChange={(event) => setSubmissionById((prev) => ({ ...prev, [id]: event.target.value }))}
                            rows={3}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                            placeholder="Type your answer or paste a file link..."
                          />
                          <button
                            type="button"
                            onClick={() => void handleSubmitHomework(record)}
                            className="mt-2 rounded-lg bg-slate-950 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
                          >
                            Submit Homework
                          </button>
                        </div>
                      )}

                      {canReview && record.status === 'SUBMITTED' && (
                        <div className="mt-4 grid gap-3 md:grid-cols-[120px_minmax(0,1fr)_auto]">
                          <input
                            type="number"
                            value={review.marks}
                            onChange={(event) => setReviewById((prev) => ({ ...prev, [id]: { ...review, marks: event.target.value } }))}
                            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
                            placeholder="Marks"
                          />
                          <input
                            value={review.remarks}
                            onChange={(event) => setReviewById((prev) => ({ ...prev, [id]: { ...review, remarks: event.target.value } }))}
                            className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
                            placeholder="Remarks"
                          />
                          <button
                            type="button"
                            onClick={() => void handleReviewHomework(record)}
                            className="rounded-lg bg-slate-950 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
                          >
                            Review
                          </button>
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export const ComplaintsReviewsPage = () => (
  <WorkflowConsole
    config={{
      kind: 'COMPLAINT',
      title: 'Complaints, Report, Block, and Review Moderation',
      eyebrow: 'Safety',
      description: 'Capture complaints and safety reports, escalate cases, track moderation notes, and preserve review integrity.',
      createLabel: 'Create Complaint or Report',
      initialStatus: 'OPEN',
      statusOptions: ['OPEN', 'TRIAGED', 'IN_REVIEW', 'ESCALATED', 'RESOLVED', 'CLOSED'],
      fields: [
        { name: 'relatedRecordId', label: 'Related Demo / Selection / Review ID', topLevel: true },
        { name: 'category', label: 'Category', type: 'select', options: ['MISLEADING_PROFILE', 'PAYMENT_ISSUE', 'NO_SHOW', 'INAPPROPRIATE_BEHAVIOUR', 'SAFETY_CONCERN', 'FAKE_QUALIFICATION', 'EXAM_ISSUE', 'SCHOLARSHIP_ISSUE', 'PRIVACY', 'OTHER'] },
        { name: 'priority', label: 'Priority', type: 'select', options: ['LOW', 'MEDIUM', 'HIGH', 'URGENT'] },
        { name: 'reportedUserId', label: 'Reported User ID' },
        { name: 'blockRequested', label: 'Block Requested', type: 'select', options: ['NO', 'YES'] },
        { name: 'evidenceUrls', label: 'Evidence URLs', type: 'textarea', placeholder: 'One URL per line for screenshots, recordings, receipts, or chat evidence' },
        { name: 'assignedToUserId', label: 'Assigned Admin / Staff User ID', topLevel: true },
        { name: 'internalNote', label: 'Internal Admin Note', type: 'textarea' },
        { name: 'escalationReason', label: 'Escalation Reason', type: 'textarea' },
        { name: 'resolutionSummary', label: 'Resolution Summary', type: 'textarea' },
        { name: 'appealReason', label: 'Appeal Reason', type: 'textarea' },
        { name: 'notes', label: 'Complaint Details', type: 'textarea' },
      ],
    }}
  />
);

export const ReviewModerationPage = () => (
  <WorkflowConsole
    config={{
      kind: 'REVIEW_MODERATION',
      title: 'Review Moderation and Rating Integrity',
      eyebrow: 'Trust and Safety',
      description: 'Moderate verified learner reviews, inspect fraud signals, track report reasons, and publish or hide tutor ratings with audit history.',
      createLabel: 'Create Review Moderation Record',
      initialStatus: 'PENDING',
      statusOptions: ['PENDING', 'FLAGGED', 'REPORTED', 'APPROVED', 'REJECTED', 'HIDDEN'],
      fields: [
        { name: 'studentUserId', label: 'Reviewer Student User ID', topLevel: true },
        { name: 'teacherUserId', label: 'Tutor User ID', topLevel: true },
        { name: 'relatedRecordId', label: 'Teacher Selection ID', topLevel: true },
        { name: 'rating', label: 'Rating', type: 'number' },
        { name: 'reviewText', label: 'Review Text', type: 'textarea' },
        { name: 'reportReason', label: 'Report Reason', type: 'select', options: ['FAKE_REVIEW', 'ABUSIVE_LANGUAGE', 'OFF_PLATFORM_CONTACT', 'PRIVACY', 'SPAM', 'CONFLICT_OF_INTEREST', 'OTHER'] },
        { name: 'verifiedLearnerReview', label: 'Verified Learner Review', type: 'select', options: ['YES', 'NO'] },
        { name: 'fraudSignals', label: 'Fraud Signals', type: 'textarea', placeholder: 'VERY_SHORT_REVIEW, MULTIPLE_LINKS, EXTREME_RATING_LOW_CONTEXT' },
        { name: 'moderationDecision', label: 'Moderation Decision', type: 'select', options: ['PUBLISH', 'HIDE', 'REQUEST_EDIT', 'ESCALATE'] },
        { name: 'note', label: 'Moderator Note', type: 'textarea' },
      ],
    }}
  />
);

export const StaffRbacPage = () => (
  <WorkflowConsole
    config={{
      kind: 'STAFF_PERMISSION',
      title: 'Staff RBAC and Permissions',
      eyebrow: 'Admin Controls',
      description: 'Create staff permission records with minimum access, separate duties, and privileged-action audit history.',
      createLabel: 'Create Staff Permission',
      initialStatus: 'ACTIVE',
      statusOptions: ['ACTIVE', 'SUSPENDED', 'REVOKED'],
      fields: [
        { name: 'assignedToUserId', label: 'Staff User ID', topLevel: true },
        { name: 'staffRole', label: 'Staff Role', type: 'select', options: ['TUTOR_VERIFICATION_STAFF', 'STUDENT_SUPPORT_STAFF', 'PAYMENT_SUPPORT_STAFF', 'COMPLAINT_RESOLUTION_STAFF', 'EXAM_MODERATION_STAFF', 'SCHOLARSHIP_STAFF', 'EDUCATION_OPERATIONS_MANAGER'] },
        { name: 'permissions', label: 'Permissions', type: 'textarea', placeholder: 'VERIFY_TUTOR, REVIEW_REFUND, RESOLVE_COMPLAINT' },
        { name: 'requiresReauth', label: 'Require Re-authentication', type: 'select', options: ['YES', 'NO'] },
      ],
    }}
  />
);

export const EducationAnalyticsPage = () => (
  <WorkflowConsole
    config={{
      kind: 'ANALYTICS_EVENT',
      title: 'Analytics Events and Reports',
      eyebrow: 'Reporting',
      description: 'Record product events and review operational metrics for search, demo, payment, subscription, class, exam, review, and complaint funnels.',
      createLabel: 'Record Analytics Event',
      initialStatus: 'RECORDED',
      statusOptions: ['RECORDED', 'PROCESSED', 'IGNORED'],
      fields: [
        { name: 'eventName', label: 'Event Name', type: 'select', options: ['EDUCATION_OPENED', 'TUTOR_SEARCH_STARTED', 'TUTOR_SAVED', 'DEMO_REQUESTED', 'DEMO_ACCEPTED', 'PAYMENT_STARTED', 'PAYMENT_COMPLETED', 'CLASS_COMPLETED', 'EXAM_SUBMITTED', 'REVIEW_SUBMITTED', 'COMPLAINT_SUBMITTED'] },
        { name: 'source', label: 'Source' },
        { name: 'metadata', label: 'Metadata', type: 'textarea' },
      ],
    }}
  />
);

export const EducationRemindersPage = () => (
  <WorkflowConsole
    config={{
      kind: 'REMINDER_JOB',
      title: 'Background Jobs and Reminders',
      eyebrow: 'Notifications',
      description: 'Schedule reminders for demos, classes, exams, subscription expiry, payments, refunds, progress reports, and document expiry.',
      createLabel: 'Schedule Reminder',
      initialStatus: 'SCHEDULED',
      statusOptions: ['SCHEDULED', 'SENT', 'FAILED', 'CANCELLED'],
      fields: [
        { name: 'recipientUserId', label: 'Recipient User ID' },
        { name: 'channel', label: 'Channel', type: 'select', options: ['IN_APP', 'PUSH', 'EMAIL', 'SMS', 'WHATSAPP'] },
        { name: 'category', label: 'Category', type: 'select', options: ['DEMO_REMINDER', 'CLASS_REMINDER', 'EXAM_REMINDER', 'PAYMENT_RETRY', 'REFUND_UPDATE', 'SUBSCRIPTION_EXPIRY', 'DOCUMENT_EXPIRY', 'PROGRESS_REPORT'] },
        { name: 'scheduledAt', label: 'Scheduled At', type: 'datetime-local', topLevel: true },
        { name: 'message', label: 'Message', type: 'textarea' },
      ],
    }}
  />
);

export const PaymentReconciliationPage = () => (
  <WorkflowConsole
    config={{
      kind: 'PAYMENT_RECONCILIATION',
      title: 'Payment Webhook, Receipt, and Reconciliation Checks',
      eyebrow: 'Payment Integrity',
      description: 'Record server-calculated amount checks, signature verification, idempotency keys, generated receipts, duplicate webhook detection, refund links, and reconciliation status.',
      createLabel: 'Create Payment Check',
      initialStatus: 'PENDING',
      statusOptions: ['PENDING', 'VERIFIED', 'PENDING_REVIEW', 'DUPLICATE', 'FAILED', 'REFUNDED', 'RECONCILED'],
      fields: [
        { name: 'webhookMode', label: 'Check Mode', type: 'select', options: ['MANUAL', 'WEBHOOK'] },
        { name: 'idempotencyKey', label: 'Idempotency Key', topLevel: true },
        { name: 'paymentProvider', label: 'Payment Provider', type: 'select', options: ['WALLET', 'RAZORPAY', 'STRIPE', 'UPI', 'CASH_ENTRY'] },
        { name: 'serverCalculatedAmount', label: 'Server Calculated Amount', type: 'number' },
        { name: 'amount', label: 'Paid Amount', type: 'number' },
        { name: 'signatureVerified', label: 'Signature Verified', type: 'select', options: ['true', 'false'] },
        { name: 'transactionId', label: 'Provider Transaction ID' },
        { name: 'notes', label: 'Reconciliation Notes', type: 'textarea' },
      ],
    }}
  />
);

export const SubscriptionEnhancementsPage: React.FC = () => {
  const { user } = useAuth();
  const role = currentUserRole(user);
  const [records, setRecords] = useState<EducationWorkflowRecord[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRecords(await educationOpsService.listRecords({ kind: 'SUBSCRIPTION_USAGE' }));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (role === 'ADMIN') {
    return (
      <WorkflowConsole
        config={{
          kind: 'SUBSCRIPTION_USAGE',
          title: 'Subscription Limits, Parent Reports, and Smart Recommendations',
          eyebrow: 'Subscriptions',
          description: 'Admin-only setup for contact limits, demo limits, report access, refund eligibility, smart recommendations, and usage status.',
          createLabel: 'Create Subscription Usage Record',
          initialStatus: 'ACTIVE',
          statusOptions: ['ACTIVE', 'LIMIT_EXCEEDED', 'REFUND_ELIGIBLE', 'EXPIRED', 'CANCELLED'],
          fields: [
            { name: 'studentUserId', label: 'Student User ID', topLevel: true },
            { name: 'relatedRecordId', label: 'Subscription ID', topLevel: true },
            { name: 'planName', label: 'Plan Name' },
            { name: 'contactLimit', label: 'Tutor Contact Limit', type: 'number' },
            { name: 'demoLimit', label: 'Demo Request Limit', type: 'number' },
            { name: 'reportLimit', label: 'Parent Report Limit', type: 'number' },
            { name: 'refundEligible', label: 'Refund Eligible', type: 'select', options: ['YES', 'NO'] },
            { name: 'smartRecommendations', label: 'Smart Recommendations', type: 'select', options: ['ENABLED', 'DISABLED'] },
          ],
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-indigo-700">Subscriptions</p>
          <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-950">My subscription usage</h1>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
            Usage records are created automatically from plan purchase or by admin operations. Students and teachers only need to monitor limits here.
          </p>
        </div>

        {loading ? (
          <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
            Loading subscription usage...
          </div>
        ) : records.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
            No subscription usage record is active yet. It will appear after a plan is purchased or assigned.
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {records.map((record) => {
              const usage = record.payload?.usage || {};
              const limits = record.payload?.limits || {};
              return (
                <article key={getEducationRecordId(record)} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h2 className="font-semibold text-slate-950">{record.payload?.planName || record.title || 'Current Plan'}</h2>
                      <p className="mt-1 text-sm text-slate-500">{record.relatedRecordId || 'Subscription usage'}</p>
                    </div>
                    <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${statusClass(record.status)}`}>
                      {record.status}
                    </span>
                  </div>
                  <div className="mt-5 space-y-3">
                    {['CONTACT', 'DEMO', 'REPORT'].map((metric) => {
                      const used = Number(usage[metric] || 0);
                      const limit = Number(limits[metric] || 0);
                      const percent = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
                      return (
                        <div key={metric}>
                          <div className="flex justify-between text-sm">
                            <span className="font-medium text-slate-700">{metric}</span>
                            <span className="text-slate-500">{used}/{limit || 'Unlimited'}</span>
                          </div>
                          <div className="mt-1 h-2 rounded-full bg-slate-100">
                            <div className="h-2 rounded-full bg-indigo-600" style={{ width: `${percent}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-4 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
                    Refund eligible: {record.payload?.refundEligible || 'Not assessed'} | Smart recommendations: {record.payload?.smartRecommendations || 'Plan default'}
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
